/**
 * Cloud media adapter — Cloudflare R2 via Worker (replaces Firebase Storage).
 * Keeps the historical `uploadEntityThemePhoto` / `deleteEntityThemePhoto` API
 * so player / org / competition services stay unchanged at the call site.
 */
import {
  deleteEntityMedia,
  isMediaApiConfigured,
  mediaObjectPath,
  uploadCompetitionBanner,
  uploadOrganizationLogo,
  uploadPlayerPhoto,
} from "../services/media/mediaService";

/** @typedef {'player' | 'organization' | 'competition'} MediaKind */

export { mediaObjectPath };

/**
 * Compress + upload; returns cache-busted public CDN URL.
 * @param {{
 *   kind: MediaKind,
 *   entityId: string,
 *   file: File,
 *   onProgress?: (phase: string, detail?: object) => void,
 * }} input
 * @returns {Promise<string>}
 */
export async function uploadEntityThemePhoto({ kind, entityId, file, onProgress }) {
  if (!isMediaApiConfigured()) {
    const err = new Error(
      "Cloud media is not configured (set REACT_APP_MEDIA_API)"
    );
    err.code = "media_api_missing";
    throw err;
  }
  let result;
  if (kind === "player") {
    result = await uploadPlayerPhoto(entityId, file, { onProgress });
  } else if (kind === "organization") {
    result = await uploadOrganizationLogo(entityId, file, { onProgress });
  } else if (kind === "competition") {
    result = await uploadCompetitionBanner(entityId, file, { onProgress });
  } else {
    throw new Error("Unknown media kind");
  }
  return result.url;
}

/**
 * @param {MediaKind} kind
 * @param {string} entityId
 */
export async function deleteEntityThemePhoto(kind, entityId) {
  if (!isMediaApiConfigured()) return;
  try {
    await deleteEntityMedia(kind, entityId);
  } catch (e) {
    const status = /** @type {{ status?: number }} */ (e)?.status;
    if (status === 404) return;
    throw e;
  }
}
